import { useState } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HardHat, Eye, EyeSlash } from "@phosphor-icons/react";

const LoginPage = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        name: "",
        company: ""
    });
    
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        let result;
        if (isLogin) {
            result = await login(formData.email, formData.password);
        } else {
            result = await register(formData);
        }

        setLoading(false);
        
        if (result.success) {
            navigate("/");
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
                style={{
                    backgroundImage: "url('https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&q=85')"
                }}
            >
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 flex flex-col justify-end p-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-14 h-14 bg-[#FFD700] rounded-sm flex items-center justify-center">
                            <HardHat size={32} weight="fill" className="text-[#111111]" />
                        </div>
                        <h1 className="font-heading font-black text-4xl text-white">Gestión de Obras - THC</h1>
                    </div>
                    <p className="text-xl text-white/90 max-w-md leading-relaxed">
                        Gestiona tu empresa de construcción de forma eficiente. Control de stock, vehículos, obras y más.
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F9FA]">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-3 justify-center mb-8">
                        <div className="w-12 h-12 bg-[#FFD700] rounded-sm flex items-center justify-center">
                            <HardHat size={28} weight="fill" className="text-[#111111]" />
                        </div>
                        <h1 className="font-heading font-black text-2xl">Gestión de Obras - THC</h1>
                    </div>

                    <div className="bg-white border border-[#E5E5E5] rounded-sm p-8">
                        <h2 className="font-heading font-bold text-2xl mb-2">
                            {isLogin ? "Iniciar sesión" : "Crear cuenta"}
                        </h2>
                        <p className="text-[#555555] mb-6">
                            {isLogin 
                                ? "Ingresa tus credenciales para acceder" 
                                : "Completa los datos para registrarte"
                            }
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isLogin && (
                                <>
                                    <div>
                                        <Label htmlFor="name" className="label-overline mb-2 block">
                                            Nombre completo
                                        </Label>
                                        <Input
                                            id="name"
                                            name="name"
                                            type="text"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input-industrial w-full"
                                            placeholder="Tu nombre"
                                            required={!isLogin}
                                            data-testid="register-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="company" className="label-overline mb-2 block">
                                            Empresa
                                        </Label>
                                        <Input
                                            id="company"
                                            name="company"
                                            type="text"
                                            value={formData.company}
                                            onChange={handleChange}
                                            className="input-industrial w-full"
                                            placeholder="Nombre de tu empresa"
                                            data-testid="register-company-input"
                                        />
                                    </div>
                                </>
                            )}

                            <div>
                                <Label htmlFor="email" className="label-overline mb-2 block">
                                    Email
                                </Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="input-industrial w-full"
                                    placeholder="tu@email.com"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div>
                                <Label htmlFor="password" className="label-overline mb-2 block">
                                    Contraseña
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="input-industrial w-full pr-12"
                                        placeholder="••••••••"
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#111111]"
                                    >
                                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full mt-6"
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
                                        {isLogin ? "Iniciando..." : "Registrando..."}
                                    </span>
                                ) : (
                                    isLogin ? "Iniciar sesión" : "Crear cuenta"
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <button
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-[#0055FF] hover:underline font-medium"
                                data-testid="toggle-auth-mode"
                            >
                                {isLogin 
                                    ? "¿No tienes cuenta? Regístrate" 
                                    : "¿Ya tienes cuenta? Inicia sesión"
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
